import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useAnnotations from "admin/hooks/useAnnotations";
import useReviewStatus from "admin/hooks/useReviewStatus";
import type { ReviewStatus } from "admin/types/interfaces/conversation";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { Button } from "~/components/ui/button";
import useNextGenHeader, { type BreadcrumbItem } from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { cn } from "~/lib/utils";
import { getAdminConversation, type AdminConversationView } from "~/services/admin-conversations";
import { AnnotationDrawer } from "./AnnotationDrawer";
import { ReadOnlyConversationView } from "./ReadOnlyConversationView";

export const AdminConversationRoute = () => {
  const { conversationId } = useParams<{ conversationId: string }>();

  if (!conversationId) return null;

  return <AdminConversation key={conversationId} conversationId={conversationId} />;
};

interface AdminConversationProps {
  conversationId: string;
}

const AdminConversation = ({ conversationId }: AdminConversationProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Track the latest review status server-side so the drawer header reflects
  // edits without waiting for the conversation poll to come back around.
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus | undefined>(undefined);
  const [bannerView, setBannerView] = useState<AdminConversationView | null | undefined>(undefined);

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const view = await getAdminConversation(conversationId, { signal });
      setBannerView(view);
      return view;
    },
    [conversationId]
  );

  useEffect(() => {
    if (bannerView?.review_status && reviewStatus === undefined) {
      setReviewStatus(bannerView.review_status);
    }
  }, [bannerView?.review_status, reviewStatus]);

  const { annotations, isLoading, createAnnotation, updateAnnotation, deleteAnnotation } =
    useAnnotations(conversationId);
  const { updateReviewStatus } = useReviewStatus(conversationId);

  // Auto-open the sidebar once on first load if there are existing notes.
  // After that, defer to whatever the user has chosen.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    if (isLoading) return;
    hasAutoOpenedRef.current = true;
    if (annotations.length > 0) setIsDrawerOpen(true);
  }, [isLoading, annotations.length]);

  const handleReviewStatusChange = useCallback(
    async (next: ReviewStatus) => {
      await updateReviewStatus(next);
      setReviewStatus(next);
    },
    [updateReviewStatus]
  );

  useDocumentTitle(bannerView?.display_name ?? "");

  const breadcrumbs = useMemo<BreadcrumbItem[]>(
    () => [
      { label: "Admin" },
      {
        label:
          bannerView === undefined
            ? "Loading…"
            : (bannerView?.display_name ?? "Conversation not found"),
      },
    ],
    [bannerView]
  );

  // Animate the header slot's width in lockstep with the drawer's own
  // w-[364px] ↔ w-0 transition (same 300ms ease-in-out) so the toggle slides
  // across as the sidebar expands/collapses. The left border only shows when
  // open so the closed state stays flush with the rest of the header.
  const rightPane = useMemo(
    () => (
      <div
        className={cn(
          "flex shrink-0 items-center justify-end overflow-hidden px-3 transition-[width] duration-300 ease-in-out",
          isDrawerOpen ? "w-[364px] border-l border-border" : "w-14"
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground"
          onClick={() => setIsDrawerOpen((open) => !open)}
          aria-label={isDrawerOpen ? "Collapse notes sidebar" : "Expand notes sidebar"}
          aria-pressed={isDrawerOpen}
        >
          {isDrawerOpen ? (
            <PanelRightClose className="size-4.5" />
          ) : (
            <PanelRightOpen className="size-4.5" />
          )}
        </Button>
      </div>
    ),
    [isDrawerOpen]
  );

  useNextGenHeader({ breadcrumbs, rightPane });

  // Always-mounted so the drawer's internal w-[364px] ↔ w-0 transition can
  // play on both open and close. Closing is owned by the page-header toggle.
  const rightPanel = (
    <AnnotationDrawer
      isOpen={isDrawerOpen}
      annotations={annotations}
      isLoading={isLoading}
      createAnnotation={createAnnotation}
      updateAnnotation={updateAnnotation}
      deleteAnnotation={deleteAnnotation}
      reviewStatus={reviewStatus}
      onReviewStatusChange={handleReviewStatusChange}
    />
  );

  return (
    <ReadOnlyConversationView
      conversationId={conversationId}
      fetcher={fetcher}
      bannerSubtitle="Admin read-only view."
      ownerVerb="Owned by"
      notFoundTitle="Conversation not found"
      notFoundBody="It may have been deleted, or the ID is wrong."
      rightPanel={rightPanel}
      readOnlyFeedback
    />
  );
};
